import { MeasureToolUnits, Units } from '@ais-ui/utils';
import { Settings } from '@ais-ui/websockets';

// Helper function to migrate legacy setting to new format
const migrateLegacyUnit = (legacyUnit: string): Units => {
  switch (legacyUnit) {
    case MeasureToolUnits.KILOMETERS:
      return Units.METRIC;
    case MeasureToolUnits.NAUTICAL_MILES:
      return Units.MARITIME;
    default:
      return Units.METRIC;
  }
};

// Helper function to get unit value with backwards compatibility and validation
export const getLengthUnit = (settings: Settings): Units => {
  const ribbonSettings = settings['ribbonPlugin.settings'];

  // Check for new setting first
  if (ribbonSettings?.length_unit) {
    // Validate that it's a valid Units value
    if (Object.values(Units).includes(ribbonSettings.length_unit)) {
      return ribbonSettings.length_unit;
    }
  }

  // Fall back to legacy setting
  if (ribbonSettings?.measure_tool_length_unit) {
    return migrateLegacyUnit(ribbonSettings.measure_tool_length_unit);
  }

  // Default to metric for any invalid or missing values
  return Units.METRIC;
};

// Helper function to detect if a unit is a length unit
export const isLengthUnit = (unit: string | undefined): boolean => {
  if (!unit) return false;

  const normalizedUnit = unit.toLowerCase().trim();

  // Check for common length unit variations
  const lengthUnits = [
    'meter',
    'meters',
    'm',
    'kilometre',
    'kilometres',
    'kilometer',
    'kilometers',
    'km',
    'centimeter',
    'centimeters',
    'centimetre',
    'centimetres',
    'cm',
    'millimeter',
    'millimeters',
    'millimetre',
    'millimetres',
    'mm',
    'foot',
    'feet',
    'ft',
    'inch',
    'inches',
    'in',
    'mile',
    'miles',
    'mi',
    'nautical mile',
    'nautical miles',
    'nm',
    'nmi',
  ];

  return lengthUnits.includes(normalizedUnit);
};

// Helper function to convert a value from any length unit to meters
export const convertToMeters = (
  value: number,
  originalUnit: string
): number => {
  const normalizedUnit = originalUnit.toLowerCase().trim();

  switch (normalizedUnit) {
    case 'km':
    case 'kilometer':
    case 'kilometers':
    case 'kilometre':
    case 'kilometres':
      return value * 1000;
    case 'cm':
    case 'centimeter':
    case 'centimeters':
    case 'centimetre':
    case 'centimetres':
      return value / 100;
    case 'mm':
    case 'millimeter':
    case 'millimeters':
    case 'millimetre':
    case 'millimetres':
      return value / 1000;
    case 'ft':
    case 'foot':
    case 'feet':
      return value * 0.3048;
    case 'in':
    case 'inch':
    case 'inches':
      return value * 0.0254;
    case 'mi':
    case 'mile':
    case 'miles':
      return value * 1609.344;
    case 'nm':
    case 'nmi':
    case 'nautical mile':
    case 'nautical miles':
      return value * 1852;
    case 'm':
    case 'meter':
    case 'meters':
    default:
      return value; // Already in meters
  }
};

export const parseValueWithUnit = (
  valueString: string | undefined
): { value: number; unit: string } | null => {
  if (!valueString) {
    return null;
  }

  // Split on space and trim
  const parts = valueString.trim().split(/\s+/);

  if (parts.length < 2) {
    return null; // No unit found
  }

  const value = parseFloat(parts[0]);
  const unit = parts.slice(1).join(' '); // Handle units like "nautical miles"

  if (isNaN(value)) {
    return null; // Invalid number
  }

  return { value, unit };
};
